/**
 * IndieChatPanelV2 — Panel / Drawer / Flyout chat surface
 *
 * Drop-in replacement for the MUI-based IndieChatPanel.js.
 * Supports the same modes (flyout | docked | page) and prop API.
 * Visual design is pixel-faithful to the Figma export.
 *
 * Usage:
 *   <IndieChatPanelV2
 *     mode="flyout"
 *     client={{ Name: 'Acme Boutique', ClientCode: '1234' }}
 *     onClose={handleClose}
 *     onToggleDock={handleDock}
 *   />
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowUpRight,
  CalendarDays,
  LayoutGrid,
  Minimize2,
  Maximize2,
  MapPin,
  X,
  ChevronDown,
} from 'lucide-react';
import './orbit-chat.css';
import { SparkleIcon } from './indie-chat.types';
import IndieChatPanel from './IndieChatPanel';
import IndieChatBody from './IndieChatBody';
import type { SaveArtifactOverrides } from './IndieGeneratedFiles';
import IndieChatInput from './IndieChatInput';
import GlobalTip from '../../providers/GlobalTip';
import { useOrbitChat } from '../../hooks/useOrbitChat';
import { useOrbitMaintenanceMessage } from '../../hooks/useOrbitMaintenanceMessage';
import { useOrbitMetadata } from '../../hooks/useOrbitMetadata';
import { useOrbitArtifacts } from '../../hooks/useOrbitArtifacts';
import { useUserPrompts } from '../../hooks/useUserPrompts';
import type { OrbitClient } from '../../services/OrbitChatService';
import type { ChatMessage } from './indie-chat.types';
import { pickOrbitContextFiles, resolveOrbitContextUploadsFromFiles } from '../../services/OrbitChatUploadUtils';
import OrbitMaintenanceBanner from './OrbitMaintenanceBanner';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ChatMode = 'flyout' | 'docked' | 'page';

interface Client extends OrbitClient {
  /** Legacy alias — maps to ClientCode */
  ClientCode?: string | null;
  Name?: string | null;
}

interface Props {
  mode?: ChatMode;
  /** Laravel client object — provides ClientCode + Name */
  client?: Client;
  /** Laravel client int ID — required for live API calls */
  clientId?: number;
  onClose?: () => void;
  onToggleDock?: () => void;
  onOpenFullView?: (conversationId?: string) => void;
  pageAssistant?: any;
}

type AssistantTab = 'ask-indie' | 'indie-insight';

// ─── Component ───────────────────────────────────────────────────────────────

const IndieChatPanelV2: React.FC<Props> = ({
  mode = 'flyout',
  client,
  clientId = 0,
  onClose,
  onToggleDock,
  onOpenFullView,
  pageAssistant,
}) => {
  const tipContext = GlobalTip.useMessage();
  const isStandalonePage = mode === 'page';
  const isInlineMode = mode === 'docked' || mode === 'page';
  const isDrawerStyledMode = mode === 'flyout' || mode === 'docked';

  const clientLabel = client?.Name
    ? client.ClientCode
      ? `${client.Name} (${client.ClientCode})`
      : client.Name
    : 'No client selected';

  const {
    messages,
    isStreaming,
    streamingMessageId,
    sendMessage,
    conversationId,
    quotaExceeded,
    submitFeedback,
    isLoadingHistory,
    contextUploads,
    addContextUploads,
    removeContextUpload,
  } = useOrbitChat({
    clientId: clientId ?? 0,
    client: client ?? {},
  });
  const { m1Prompts } = useOrbitMetadata(clientId ?? 0);
  const { addPrompt } = useUserPrompts(clientId ?? 0);
  const { artifacts, saveArtifact } = useOrbitArtifacts(clientId ?? 0);
  const { banner: maintenanceBanner } = useOrbitMaintenanceMessage(clientId ?? 0);
  const maintenanceModeActive = Boolean(
    maintenanceBanner.is_enabled && String(maintenanceBanner.message || '').trim(),
  );
  const hasPageAssistant = Boolean(pageAssistant?.enabled);
  const [activeAssistantTab, setActiveAssistantTab] = useState<AssistantTab>('ask-indie');

  useEffect(() => {
    if (!hasPageAssistant) {
      setActiveAssistantTab('ask-indie');
    }
  }, [hasPageAssistant]);

  useEffect(() => {
    if (hasPageAssistant && pageAssistant?.focusRequestToken) {
      setActiveAssistantTab('indie-insight');
    }
  }, [hasPageAssistant, pageAssistant?.focusRequestToken]);

  const reportAssistantClientId = useMemo(() => (
    pageAssistant?.clientId ?? clientId ?? 0
  ), [clientId, pageAssistant?.clientId]);

  const handleAttachFile = async () => {
    const selectedFiles = await pickOrbitContextFiles();
    if (selectedFiles.length === 0) {
      return;
    }

    const { uploads, errors } = await resolveOrbitContextUploadsFromFiles(selectedFiles, {
      clientId: clientId && clientId > 0 ? clientId : undefined,
      conversationId,
    });

    if (uploads.length > 0) {
      addContextUploads(uploads);
      tipContext.showSuccess(
        uploads.length === 1
          ? 'File added to the current chat context'
          : `${uploads.length} files added to the current chat context`,
      );
    }

    if (errors.length > 0) {
      tipContext.show(errors[0]);
    }
  };

  const formatSavedLabel = React.useCallback((content: string, prefix = 'Saved prompt') => {
    const singleLine = content.replace(/\s+/g, ' ').trim();
    if (!singleLine) return prefix;
    return singleLine.length > 60 ? `${singleLine.slice(0, 57)}…` : singleLine;
  }, []);

  const handleSavePrompt = React.useCallback(async (message: ChatMessage) => {
    try {
      await addPrompt({
        label: formatSavedLabel(message.content, message.role === 'assistant' ? 'Assistant prompt' : 'My prompt'),
        promptText: message.content,
      });
      tipContext.showSuccess('Saved to My Prompts');
    } catch {
      tipContext.show('Unable to save this prompt right now.');
    }
  }, [addPrompt, formatSavedLabel, tipContext]);

  const handleSaveArtifact = React.useCallback(async (message: ChatMessage, overrides?: SaveArtifactOverrides) => {
    try {
      await saveArtifact({
        title: formatSavedLabel(message.content, 'Saved analysis'),
        artifact_type: 'summary',
        content_markdown: message.content,
        conversation_id: conversationId,
        source_message_id: Number(String(message.id).replace(/^hist-/, '')) || undefined,
        ...overrides,
      });
      tipContext.showSuccess('Artifact saved');
    } catch (err) {
      tipContext.show('Unable to save this artifact right now.');
      throw err;
    }
  }, [conversationId, formatSavedLabel, saveArtifact, tipContext]);

  const sendAskIndieMessage = React.useCallback((text: string) => {
    if (maintenanceModeActive) {
      tipContext.show('Ask Indie is temporarily unavailable while maintenance is enabled.');
      return;
    }

    sendMessage(text);
  }, [maintenanceModeActive, sendMessage, tipContext]);

  const renderAskIndieSurface = () => (
    <>
      {maintenanceBanner.is_enabled && maintenanceBanner.message ? (
        <OrbitMaintenanceBanner message={maintenanceBanner.message} />
      ) : null}
      <IndieChatBody
        messages={messages}
        onSuggestionClick={sendAskIndieMessage}
        streamingMessageId={streamingMessageId}
        isStreaming={isStreaming || isLoadingHistory}
        context={isDrawerStyledMode ? "Management One's AI advisor" : clientLabel}
        conversationId={conversationId}
        clientId={clientId}
        quotaExceeded={quotaExceeded}
        onFeedback={submitFeedback}
        onSavePrompt={handleSavePrompt}
        onSaveArtifact={handleSaveArtifact}
        artifacts={artifacts}
        surface={isDrawerStyledMode ? 'drawer' : 'default'}
        startPrompts={m1Prompts}
        hideStartPrompts={maintenanceModeActive}
        disableSuggestionClicks={maintenanceModeActive}
      />
      <IndieChatInput
        onSend={sendAskIndieMessage}
        onAttachFile={handleAttachFile}
        attachedUploads={contextUploads}
        onRemoveUpload={removeContextUpload}
        placeholder={isDrawerStyledMode ? 'How can I help you today?' : 'Ask Indie anything about this client…'}
        disabled={maintenanceModeActive}
        isStreaming={isStreaming || isLoadingHistory}
        surface={isDrawerStyledMode ? 'drawer' : 'default'}
        onOpenFullView={() => onOpenFullView?.(conversationId)}
      />
    </>
  );

  // Container sizing by mode
  const containerClass = [
    'indie-chat flex flex-col overflow-hidden',
    isDrawerStyledMode
      ? 'rounded-xl border border-[#D5D7DA] bg-white shadow-[0_1px_2px_rgba(10,13,18,0.05)]'
      : 'rounded-[16px] shadow-[0_10px_35px_rgba(40,22,60,0.14)] border border-[rgba(116,88,143,0.22)]',
    isStandalonePage ? 'h-full w-full' : 'h-full',
  ].join(' ');

  return (
    <div className={containerClass}>
      {isDrawerStyledMode ? (
        <>
          <div className="flex items-start justify-between gap-3 p-6">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-[#EEEDFE]">
                <SparkleIcon />
              </div>
              <div className="flex flex-col gap-1.5 min-w-0">
                <h2 className="text-[16px] font-semibold leading-none text-[#333333]">New Conversation</h2>
                <p className="text-xs text-[#6B7280] leading-4 tracking-[0.12px]">40 years of M1 expertise · Dashboard</p>
              </div>
            </div>
            {!isStandalonePage && (
              <div className="flex items-center gap-1 flex-shrink-0">
                {onToggleDock && (
                  <button
                    onClick={isInlineMode ? onToggleDock : () => onOpenFullView?.(conversationId)}
                    title={isInlineMode ? 'Return to flyout' : 'Open full view'}
                    className="p-1 rounded-md hover:bg-[#F2F4F7] transition-colors"
                  >
                    {isInlineMode
                      ? <Minimize2 className="w-4 h-4 text-[#A4A7AE]" />
                      : <ArrowUpRight className="w-4 h-4 text-[#A4A7AE]" />
                    }
                  </button>
                )}
                {onClose && (
                  <button
                    onClick={onClose}
                    title="Close chat"
                    className="p-1 rounded-md hover:bg-[#F2F4F7] transition-colors"
                  >
                    <X className="w-4 h-4 text-[#A4A7AE]" />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3.5 px-6 py-3 border-y border-[#E9EAEB] bg-[#FAFAFA]">
            <span className="text-xs text-[#535862] leading-4 tracking-[0.12px]">CONTEXT</span>
            <div className="flex items-center gap-2.5 flex-wrap min-w-0">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-[0.5px] border-[#9E77ED] bg-[#F9F5FF] text-[10px] text-[#7F56D9] leading-4 tracking-[0.1px]">
                <LayoutGrid className="w-3 h-3" />
                Dashboard
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-[0.5px] border-[#E5E7EB] bg-[#FAFAFA] text-[10px] text-[#6B7280] leading-4 tracking-[0.1px]">
                <CalendarDays className="w-3 h-3" />
                May 2026
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-[0.5px] border-[#E5E7EB] bg-[#FAFAFA] text-[10px] text-[#6B7280] leading-4 tracking-[0.1px]">
                <MapPin className="w-3 h-3" />
                All locations
              </span>
            </div>
          </div>
        </>
      ) : (
        <div
          className="flex items-start justify-between px-[18px] py-[14px] border-b border-[rgba(255,255,255,0.08)]"
          style={{ background: 'linear-gradient(135deg, #4d3660 0%, #2d1f3f 100%)' }}
        >
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-2.5">
              <div className="w-[34px] h-[34px] flex items-center justify-center rounded-[12px] bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.18)] flex-shrink-0">
                <SparkleIcon />
              </div>
              <div>
                <p className="text-white text-[15px] font-bold leading-[1.2]">Ask Indie</p>
                <p className="text-[rgba(255,255,255,0.75)] text-[11px]">Retail consultant chat surface</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 self-start px-[9px] py-[3px] rounded-full bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.18)]">
              <ChevronDown className="w-3 h-3 text-[rgba(255,255,255,0.7)]" />
              <span className="text-white text-[11px] font-medium">{clientLabel}</span>
            </div>
          </div>
          {!isStandalonePage && (
            <div className="flex items-center gap-1 flex-shrink-0 mt-1">
              {onToggleDock && (
                <button
                  onClick={onToggleDock}
                  title={isInlineMode ? 'Return to flyout' : 'Embed on page'}
                  className="w-7 h-7 flex items-center justify-center rounded-[7px] text-white hover:bg-[rgba(255,255,255,0.12)] transition-colors"
                >
                  {isInlineMode
                    ? <Minimize2 className="w-3.5 h-3.5" />
                    : <Maximize2 className="w-3.5 h-3.5" />
                  }
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  title="Close chat"
                  className="w-7 h-7 flex items-center justify-center rounded-[7px] text-white hover:bg-[rgba(255,255,255,0.12)] transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Chat body ── */}
      <div className="flex-1 overflow-hidden flex flex-col bg-white">
        {hasPageAssistant && (
          <div className="px-4 pt-4 pb-2 border-b border-[#E9EAEB] bg-white">
            <div className="inline-flex rounded-xl border border-[#E9EAEB] bg-[#FAFAFA] p-1 gap-1">
              {[
                { id: 'ask-indie', label: 'Ask Indie' },
                { id: 'indie-insight', label: 'Indie Insight' },
              ].map((tab) => {
                const isActive = activeAssistantTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveAssistantTab(tab.id as AssistantTab)}
                    className={`px-3.5 py-2 rounded-[10px] text-sm font-semibold transition-colors ${isActive ? 'bg-[#5E3D79] text-white shadow-sm' : 'text-[#535862] hover:bg-white'}`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!hasPageAssistant || activeAssistantTab === 'ask-indie'
          ? renderAskIndieSurface()
          : (
            <div className="flex-1 min-h-0 overflow-hidden bg-[linear-gradient(180deg,#fcfbff_0%,#f6f1fb_100%)] p-4">
              <IndieChatPanel
                mode="page"
                client={client}
                onClose={undefined}
                onToggleDock={undefined}
                reportAssistant={{
                  ...pageAssistant,
                  enabled: true,
                  clientId: reportAssistantClientId,
                }}
                embedded
              />
            </div>
          )}
      </div>
    </div>
  );
};

/**
 * SPS-5749: keyed on the selected client so React remounts the whole chat when
 * the client changes. Every piece of per-client state (conversation id,
 * transcript, uploads, history list, drawer state, input draft) is dropped in
 * one step, and the unmount cleanup in useOrbitChat aborts a stream still
 * running for the previous client. The key lives here, on the export, so every
 * mount site (layouts and pages) is covered, including future ones.
 */
const IndieChatPanelV2ForClient: React.FC<Props> = (props) => (
  <IndieChatPanelV2 key={props.clientId ?? 0} {...props} />
);
IndieChatPanelV2ForClient.displayName = 'IndieChatPanelV2';

export default IndieChatPanelV2ForClient;